/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { FC, useState, useEffect, useRef, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../root'
import { toast } from '../../../services/toast'
import { getFileDiffToSop } from '../../../services/api/files'
import Modal from '../../Modal'
import PdfViewer from '../../PdfViewer'

interface FileDiffModalProps {
  fileId: number | string
  isDetailed?: boolean
  onClose: () => void
}

interface PdfLocationResponse {
  url: string
  uuid?: string
  [key: string]: any
}

/**
 * File Diff/Compare Modal
 *
 * Shows comparison between current file and SOP template in PDF format.
 * Supports two view modes: Overview (simplified) and Detailed (full comparison).
 *
 * Based on: AngularJS FileDiffModal (app/view/app/viewer/show-file-diff-modal/)
 *
 * Features:
 * - Toggle between overview and detailed comparison views
 * - PDF viewer with dynamic sizing
 * - Download diff PDF functionality
 * - Responsive modal design
 */
export const FileDiffModal: FC<FileDiffModalProps> = ({ fileId, isDetailed = false, onClose }) => {
  const { t } = useTranslation()
  const { api } = useServiceContext()
  const [isOpen, setIsOpen] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [pdfLocation, setPdfLocation] = useState<string | null>(null)
  const [currentMode, setCurrentMode] = useState(isDetailed)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [viewerHeight, setViewerHeight] = useState<string>('')
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const refHeader = useRef<HTMLDivElement>(null)
  const refFooter = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadPdfDiff()
  }, [fileId, currentMode])

  useEffect(() => {
    // Adjust modal height dynamically on window resize
    const handleResize = () => {
      if (modalRef.current && iframeRef.current) {
        const windowHeight = window.innerHeight
        const headerHeight = 60 // Modal header height
        const footerHeight = 70 // Modal footer height
        const padding = 40 // Additional padding
        const availableHeight = windowHeight - headerHeight - footerHeight - padding
        iframeRef.current.style.height = `${Math.max(400, availableHeight)}px`
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useLayoutEffect(() => {
    if (isOpen && refHeader.current && refFooter.current) {
      const height = window.innerHeight - (refHeader.current.offsetHeight + refFooter.current.offsetHeight)
      setViewerHeight(height + 'px')
    }
  }, [isOpen])

  const loadPdfDiff = async () => {
    setIsLoading(true)
    setErrorMessage(null)
    try {
      // Call API to get PDF diff location
      const response: PdfLocationResponse = await getFileDiffToSop(Number(fileId), currentMode)

      // Process location info (similar to postprocessLocationInfo in legacy)
      let pdfUrl = response.url || response.uuid || ''

      // If uuid is returned, construct PDF URL
      if (response.uuid && !pdfUrl.startsWith('http')) {
        pdfUrl = `/api/files/pdf/${response.uuid}`
      }

      setPdfLocation(pdfUrl)
    } catch (error: any) {
      console.error('Failed to load file diff:', error)
      const errorMsg = error?.message || error?.body?.message || error?.statusText || t('FAILED_TO_LOAD_FILE_DIFF')
      setErrorMessage(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleView = () => {
    // Toggle between overview and detailed mode
    const newMode = !currentMode
    setCurrentMode(newMode)
  }

  const handleDownload = async () => {
    try {
      // Download the diff PDF
      const response = await getFileDiffToSop(Number(fileId), currentMode)

      let downloadUrl = response.url || response.uuid || ''
      if (response.uuid && !downloadUrl.startsWith('http')) {
        downloadUrl = `/api/files/pdf/${response.uuid}`
      }

      // Trigger download
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `file-diff-${fileId}-${currentMode ? 'detailed' : 'overview'}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error: any) {
      console.error('Failed to download file diff:', error)
      const errorMessage = error?.message || error?.body?.message || error?.statusText || t('DOWNLOAD_FAILED')
      toast.error(errorMessage)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <Modal
      show={isOpen}
      handleClose={onClose}
      title={t(currentMode ? 'FILE_DIFF_DETAILED_TITLE' : 'FILE_DIFF_TITLE')}
      size="md"
      className="view-changes-modal"
    >
      <div ref={refHeader as React.RefObject<HTMLDivElement>}>
        <Modal.Body>
          <div className="file-diff-modal">
            {isLoading ? (
              <div className="text-center p-5">
                <div className="spinner-border" role="status">
                  <span className="sr-only">{t('LOADING')}...</span>
                </div>
              </div>
            ) : errorMessage ? (
              <div className="alert alert-danger m-4">
                <h4>{t('ERROR')}</h4>
                <p>{errorMessage}</p>
              </div>
            ) : (
              <>
                {pdfLocation ? (
                  <div className="d-flex align-self-stretch flex-grow" style={{ minHeight: viewerHeight || undefined }}>
                    <PdfViewer src={pdfLocation} className="doc-pdf-container d-flex align-self-stretch flex-grow" />
                  </div>
                ) : (
                  <div className="alert alert-warning m-4">
                    <h4>{t('NO_DIFF_AVAILABLE')}</h4>
                    <p>{t('NO_DIFF_AVAILABLE_MESSAGE') || 'No comparison data available for this file.'}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </Modal.Body>
      </div>
      <div ref={refFooter as React.RefObject<HTMLDivElement>}>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={handleToggleView} disabled={!!errorMessage || isLoading}>
            {t(currentMode ? 'FILE_DIFF_SHOW_OVERVIEW' : 'FILE_DIFF_SHOW_DETAILED')}
          </button>
          <button
            className="btn btn-info"
            onClick={handleDownload}
            disabled={!pdfLocation || isLoading || !!errorMessage}
          >
            {t('DOWNLOAD')}
          </button>
          <button className="btn btn-primary" onClick={handleClose}>
            {t('OK')}
          </button>
        </Modal.Footer>
      </div>
    </Modal>
  )
}

export default FileDiffModal
